import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { coursesService } from '../../services/api';

import type { AdminCourseFaq, CourseFaqCategoryAdmin } from '../../types';



const CATEGORY_OPTS: Array<{ value: CourseFaqCategoryAdmin; label: string }> = [

  { value: 'LEARNING', label: 'فرآیند یادگیری' },

  { value: 'ACCESS', label: 'دسترسی و محتوا' },

  { value: 'PAYMENT', label: 'پرداخت و پشتیبانی' },

];



export default function CourseFaqsTab({ courseId }: { courseId: string }) {

  const [rows, setRows] = useState<AdminCourseFaq[]>([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState('');

  const [busyId, setBusyId] = useState<string | null>(null);



  const [creating, setCreating] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);

  const [question, setQuestion] = useState('');

  const [answer, setAnswer] = useState('');

  const [category, setCategory] = useState<CourseFaqCategoryAdmin>('LEARNING');

  const [sortOrder, setSortOrder] = useState('0');

  const [published, setPublished] = useState(true);



  const load = useCallback(async () => {

    setLoading(true);

    setError('');

    try {

      const data = await coursesService.getFaqsAdmin(courseId);

      setRows(Array.isArray(data) ? data : []);

    } catch (e: unknown) {

      setError((e as Error)?.message || 'خطا در بارگذاری سوالات');

    } finally {

      setLoading(false);

    }

  }, [courseId]);



  useEffect(() => {

    void load();

  }, [load]);



  const sorted = useMemo(

    () => [...rows].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.id.localeCompare(b.id)),

    [rows],

  );



  const resetForm = () => {

    setCreating(false);

    setEditingId(null);

    setQuestion('');

    setAnswer('');

    setCategory('LEARNING');

    setSortOrder('0');

    setPublished(true);

  };



  const startEdit = (r: AdminCourseFaq) => {

    setEditingId(r.id);

    setCreating(false);

    setQuestion(r.question);

    setAnswer(r.answer);

    setCategory(r.category);

    setSortOrder(String(r.sortOrder ?? 0));

    setPublished(r.published);

  };



  const submitCreate = async (e: React.FormEvent) => {

    e.preventDefault();

    if (!question.trim() || !answer.trim()) {

      setError('سوال و پاسخ را پر کنید');

      return;

    }

    setBusyId('_new');

    setError('');

    try {

      await coursesService.createFaq(courseId, {

        question: question.trim(),

        answer: answer.trim(),

        category,

        sortOrder: Number.parseInt(sortOrder, 10) || 0,

        published,

      });

      resetForm();

      await load();

    } catch (err: unknown) {

      const msg = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message;

      setError(typeof msg === 'string' ? msg : 'خطا در ثبت');

    } finally {

      setBusyId(null);

    }

  };



  const submitUpdate = async (e: React.FormEvent) => {

    e.preventDefault();

    if (!editingId || !question.trim() || !answer.trim()) {

      setError('سوال و پاسخ را پر کنید');

      return;

    }

    setBusyId(editingId);

    setError('');

    try {

      await coursesService.patchFaq(courseId, editingId, {

        question: question.trim(),

        answer: answer.trim(),

        category,

        sortOrder: Number.parseInt(sortOrder, 10) || 0,

        published,

      });

      resetForm();

      await load();

    } catch (err: unknown) {

      const msg = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message;

      setError(typeof msg === 'string' ? msg : 'خطا در به‌روزرسانی');

    } finally {

      setBusyId(null);

    }

  };



  const doDelete = async (id: string) => {

    if (!window.confirm('این سوال حذف شود؟')) return;

    setBusyId(id);

    setError('');

    try {

      await coursesService.deleteFaq(courseId, id);

      if (editingId === id) resetForm();

      await load();

    } catch (err: unknown) {

      const msg = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message;

      setError(typeof msg === 'string' ? msg : 'خطا در حذف');

    } finally {

      setBusyId(null);

    }

  };



  const categoryLabel = (c: CourseFaqCategoryAdmin) =>

    CATEGORY_OPTS.find((x) => x.value === c)?.label ?? c;



  if (loading && rows.length === 0) {

    return (

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-600">

        در حال بارگذاری…

      </div>

    );

  }



  return (

    <div className="space-y-4">

      {error && (

        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{error}</div>

      )}



      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">

        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">

          <h4 className="text-sm font-bold text-slate-800">

            {creating ? 'سوال جدید' : editingId ? 'ویرایش سوال' : 'افزودن سوال متداول'}

          </h4>

          {!creating && !editingId ? (

            <button

              type="button"

              onClick={() => {

                resetForm();

                setCreating(true);

              }}

              className="rounded-lg bg-[#1C3B9B] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#15307d]"

            >

              + سوال جدید

            </button>

          ) : (

            <button

              type="button"

              onClick={resetForm}

              className="text-xs font-medium text-slate-600 hover:text-slate-900"

            >

              انصراف

            </button>

          )}

        </div>



        {(creating || editingId) && (

          <form onSubmit={editingId ? submitUpdate : submitCreate} className="space-y-3">

            <div>

              <label className="mb-1 block text-xs font-medium text-slate-600">سوال</label>

              <textarea

                value={question}

                onChange={(e) => setQuestion(e.target.value)}

                rows={2}

                className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"

              />

            </div>

            <div>

              <label className="mb-1 block text-xs font-medium text-slate-600">پاسخ</label>

              <textarea

                value={answer}

                onChange={(e) => setAnswer(e.target.value)}

                rows={4}

                className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"

              />

            </div>

            <div className="grid gap-3 sm:grid-cols-2">

              <div>

                <label className="mb-1 block text-xs font-medium text-slate-600">دسته</label>

                <select

                  value={category}

                  onChange={(e) => setCategory(e.target.value as CourseFaqCategoryAdmin)}

                  className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"

                >

                  {CATEGORY_OPTS.map((o) => (

                    <option key={o.value} value={o.value}>

                      {o.label}

                    </option>

                  ))}

                </select>

              </div>

              <div>

                <label className="mb-1 block text-xs font-medium text-slate-600">ترتیب نمایش</label>

                <input

                  type="number"

                  value={sortOrder}

                  onChange={(e) => setSortOrder(e.target.value)}

                  className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"

                />

              </div>

            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700">

              <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} />

              منتشر شده (در سایت دیده شود)

            </label>

            <button

              type="submit"

              disabled={!!busyId}

              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"

            >

              {busyId ? 'در حال ذخیره…' : editingId ? 'ذخیره تغییرات' : 'ثبت سوال'}

            </button>

          </form>

        )}

      </div>



      <ul className="space-y-2">

        {sorted.map((r) => (

          <li

            key={r.id}

            className="flex flex-wrap items-start justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3"

          >

            <div className="min-w-0 flex-1">

              <p className="font-semibold text-slate-900">{r.question}</p>

              <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{r.answer}</p>

              <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-500">

                <span className="rounded bg-white px-2 py-0.5 border border-slate-200">{categoryLabel(r.category)}</span>

                <span>ترتیب: {r.sortOrder}</span>

                <span>{r.published ? 'منتشر' : 'پیش‌نویس'}</span>

              </div>

            </div>

            <div className="flex shrink-0 gap-2">

              <button

                type="button"

                onClick={() => startEdit(r)}

                disabled={!!busyId}

                className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs hover:bg-slate-100 disabled:opacity-50"

              >

                ویرایش

              </button>

              <button

                type="button"

                onClick={() => void doDelete(r.id)}

                disabled={busyId === r.id}

                className="rounded-lg border border-rose-200 bg-white px-2 py-1 text-xs text-rose-700 hover:bg-rose-50 disabled:opacity-50"

              >

                حذف

              </button>

            </div>

          </li>

        ))}

      </ul>



      {!loading && sorted.length === 0 && (

        <p className="text-center text-sm text-slate-500">هنوز سوالی اضافه نشده؛ از فرم بالا یک مورد بسازید.</p>

      )}

    </div>

  );

}



